// Lib
import { useCallback } from 'react';
import { Editor } from '@tiptap/react';
import { useSelector } from 'react-redux';

// Selectors
import { getPlatformDetailsSelector } from '../../platform/platformSelector';

// Actions
import { setActiveTiptapEditor } from './store/tiptapEditorStoreActions';

// Hooks
import useElementTiptapEditor, {
    UseTiptapEditorProps,
    UseTiptapEditorReturn,
} from '../../../common/tiptap/useElementTiptapEditor';
import { useTiptapEditorDispatch } from './store/tiptapEditorStoreHooks';

/**
 * Dispatches an editor update to the Tiptap editor redux store.
 */
const useDispatchTiptapActiveEditorStoreUpdate = () => {
    const dispatch = useTiptapEditorDispatch();

    return useCallback(
        (editor: Editor | null) => {
            dispatch(setActiveTiptapEditor(editor));
        },
        [dispatch],
    );
};

/**
 * Configures the Tiptap editor for use in the client.
 *
 * Thin wrapper around useElementTiptapEditor, which is in the common module and so can't include client code.
 */
export const useClientElementTiptapEditor = (
    elementTiptapEditorArgs: Omit<UseTiptapEditorProps, 'updateActiveEditorStore' | 'platformDetails'>,
): UseTiptapEditorReturn => {
    const platformDetails = useSelector(getPlatformDetailsSelector);
    const updateActiveEditorStore = useDispatchTiptapActiveEditorStoreUpdate();
    return useElementTiptapEditor({
        ...elementTiptapEditorArgs,
        updateActiveEditorStore,
        platformDetails,
    });
};

export default useClientElementTiptapEditor;
